import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useReactFlow } from "@xyflow/react";
import { Node } from "@xyflow/react";
import {
  moveSequenceSelection as moveSequenceSelectionAction,
  selectSelectedElement,
  selectSelectedElementType,
  selectSelectedTakeId,
  selectTakes,
  selectReferenceLanguage,
} from "../../app/slices/TrainingSlice";
import Take from "../../models/Take";
import {
  moveSequenceSelection,
  resolveSequenceLocator,
  SequenceLocator,
  SequenceSelectionIds,
} from "../../app/slices/helpers/SequenceMove";
import Interaction, {
  InteractionType,
} from "../../models/interactions/Interaction";
import {
  ISequence,
  SequenceCommentZone,
  SequenceTextbox,
} from "../../models/interactions/Sequence";
import { CopyPasteContext } from "../training_timeline/CopyPasteContext";
import {
  CLIPBOARD_VERSION,
  createClipboardPasteTransactionId,
  readClipboardEnvelopeFromClipboard,
  SequenceNodesPayload,
  writeClipboard,
} from "../training_timeline/copyPasteClipboard";
import type { ClipboardEnvelope } from "../training_timeline/copyPasteClipboard";
import { toast } from "react-toastify";
import { useCopyInteraction } from "../training_timeline/CopyInteractionHook";
import type { LocalizationCopySource } from "../training_timeline/CopyInteractionHook";
import { SequenceNodeData } from "./SequenceNode";
import { v4 as uuidv4 } from "uuid";
import { useUpdateActiveSequence } from "./useUpdateActiveSequence.hook";
import { TEMP_TARGET_NODE_ID } from "./SequenceCanvasTemporaryElements";
import {
  isProtectedSequenceStructureInteraction,
  isProtectedTakeSequenceTrigger,
  isSequenceRootNode,
} from "./takeSequenceEditor";
import type { SequenceCanvasSelection } from "./SequenceCanvasSelection.hook";
import {
  beginHistoryTransaction,
  cancelHistoryTransaction,
  commitHistoryTransaction,
} from "../../app/slices/HistorySlice";
import {
  buildClipboardLocalizationSnapshot,
  getClipboardLocalizationDiagnostics,
} from "../training_timeline/clipboardLocalization";
import { selectTrainingLocalizationState } from "../../app/slices/TrainingLocalizationSlice";
import type { RootState } from "../../app/Store";
import { flushPendingTextEdits } from "../../utils/pendingTextEdits";
import {
  buildClipboardDependencyMetadata,
  formatClipboardDependencyWarning,
  resolveClipboardDependencies,
  type ClipboardDependencyResolution,
} from "../training_timeline/clipboardDependencies";

const PASTE_OFFSET = { x: 50, y: 50 };

/** Edge shape we build when pasting; assignable to Edge */
type PastedEdge = {
  id: string;
  source: string;
  target: string;
  sourceHandle: string | null;
  targetHandle: string | null;
  type: "custom";
};

function createSequenceClipboardNode(
  id: string,
  position: { x: number; y: number },
  interaction: Interaction,
  options?: {
    color?: string;
  },
): Node<SequenceNodeData> {
  return {
    id,
    type: "sequence",
    dragHandle: ".draggable-header",
    position,
    data: {
      id,
      name: interaction.name,
      interaction,
      ...(options?.color != null && { color: options.color }),
    },
  };
}

export function useSequenceCopyPaste(
  selectedNodes: Node<SequenceNodeData>[],
  selectedCommentZoneIds: string[],
  selectedTextboxIds: string[] = [],
  setSelection?: (selection: SequenceCanvasSelection) => void,
) {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const copyPasteContext = useContext(CopyPasteContext);
  const interaction = useSelector(selectSelectedElement) as ISequence;
  const selectedElementType = useSelector(selectSelectedElementType);
  const selectedTakeId = useSelector(selectSelectedTakeId);
  const takes = (useSelector(selectTakes) ?? []) as Take[];
  const { updateSequence } = useUpdateActiveSequence();
  const { getInteractionCopy } = useCopyInteraction();
  const { screenToFlowPosition } = useReactFlow();
  const containerRef = useRef<HTMLDivElement | null>(null);
  /** Last mouse position (screen coords) over the canvas; used for paste-at-cursor */
  const lastMouseScreenRef = useRef<{ x: number; y: number } | null>(null);
  const edges = interaction?.edges ?? [];
  const commentZones = interaction?.commentZones ?? [];
  const textboxes = interaction?.textboxes ?? [];

  const buildDependencyMetadata = useCallback(
    (value: unknown) =>
      buildClipboardDependencyMetadata(
        value,
        store.getState(),
        selectedTakeId && interaction?.id
          ? {
              kind: "sequence",
              takeId: selectedTakeId,
              sequenceId: interaction.id,
            }
          : undefined,
      ),
    [interaction?.id, selectedTakeId, store],
  );

  const buildLocalizationSnapshot = useCallback(
    (value: unknown) => {
      flushPendingTextEdits();
      const state = store.getState();
      const localization = selectTrainingLocalizationState(state);
      return buildClipboardLocalizationSnapshot(
        value,
        localization.localizedEntries,
        selectReferenceLanguage(state),
        localization.currentLanguage.shortcode,
      );
    },
    [store],
  );

  const getLocalizationSource = useCallback(
    (envelope: ClipboardEnvelope): LocalizationCopySource =>
      envelope.version === 2
        ? { mode: "clipboardV2", snapshot: envelope.localization }
        : { mode: "currentTraining" },
    [],
  );

  const showLocalizationWarnings = useCallback(
    (value: unknown, envelope: ClipboardEnvelope) => {
      if (envelope.version !== 2) return;
      const localization = selectTrainingLocalizationState(store.getState());
      const diagnostics = getClipboardLocalizationDiagnostics(
        value,
        envelope.localization,
        localization.localizedLanguages.map((language) => language.shortcode),
      );
      if (diagnostics.missingKeys.length > 0) {
        toast.warn(
          "Some copied localized values were missing and were pasted blank",
          { position: "top-right" },
        );
      } else if (diagnostics.hasNoSharedLanguage) {
        toast.warn(
          "The copied text has no language in common with this training and was pasted blank",
          { position: "top-right" },
        );
      }
    },
    [store],
  );

  const showPasteOutcome = useCallback(
    (
      successMessage: string,
      resolution: ClipboardDependencyResolution,
      sourceValue: unknown,
      envelope: ClipboardEnvelope,
    ) => {
      if (resolution.clearedCount > 0) {
        toast.warn(formatClipboardDependencyWarning(resolution.cleared), {
          position: "top-right",
        });
        return;
      }
      showLocalizationWarnings(sourceValue, envelope);
      toast.success(successMessage, { position: "top-right" });
    },
    [showLocalizationWarnings],
  );
  const currentSelectedNodes = useMemo(() => {
    const currentNodeById = new Map(
      (interaction?.nodes ?? []).map((node) => [node.id, node]),
    );

    return selectedNodes.map((selectedNode) => {
      const currentNode = currentNodeById.get(selectedNode.id);
      if (!currentNode) return selectedNode;

      return {
        ...selectedNode,
        ...currentNode,
        // Measurements only exist in React Flow's local node state. Keep them
        // while taking interaction data and position from the live Sequence.
        measured: selectedNode.measured ?? currentNode.measured,
        width: selectedNode.width ?? currentNode.width,
        height: selectedNode.height ?? currentNode.height,
      };
    });
  }, [interaction?.nodes, selectedNodes]);

  useEffect(() => {
    const pending = copyPasteContext?.pendingCut;
    if (pending?.kind !== "sequenceSelection") return;
    const source = resolveSequenceLocator(takes, pending.source);
    if (!source) {
      copyPasteContext?.clearPendingCut?.();
      return;
    }
    const nodeIds = new Set((source.nodes ?? []).map((node) => node.id));
    const zoneIds = new Set((source.commentZones ?? []).map((zone) => zone.id));
    const textboxIds = new Set((source.textboxes ?? []).map((item) => item.id));
    if (
      pending.selection.nodeIds.some((id) => !nodeIds.has(id)) ||
      pending.selection.commentZoneIds.some((id) => !zoneIds.has(id)) ||
      pending.selection.textboxIds.some((id) => !textboxIds.has(id))
    ) {
      copyPasteContext?.clearPendingCut?.();
    }
  }, [copyPasteContext, takes]);

  const getActiveLocator = useCallback((): SequenceLocator | null => {
    if (!interaction || interaction.type !== InteractionType.Sequence)
      return null;
    return {
      kind:
        selectedElementType === "takeSequence" ? "takeSequence" : "interaction",
      takeId: selectedTakeId,
      sequenceId: interaction.id,
    };
  }, [interaction, selectedElementType, selectedTakeId]);

  const getEligibleNodes = useCallback(
    () =>
      currentSelectedNodes.filter(
        (node) =>
          node.id !== TEMP_TARGET_NODE_ID &&
          !isSequenceRootNode(node) &&
          !isProtectedTakeSequenceTrigger(node, selectedElementType),
      ),
    [currentSelectedNodes, selectedElementType],
  );

  const setSequenceCanvasRef = useCallback((el: HTMLDivElement | null) => {
    containerRef.current = el;
  }, []);

  const onPaneMouseMove = useCallback((e: React.MouseEvent) => {
    lastMouseScreenRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  useEffect(() => {
    if (!copyPasteContext || !containerRef.current) return;
    const el = containerRef.current;
    const onFocus = () => copyPasteContext.setPasteContext("sequence");
    el.addEventListener("focus", onFocus);
    return () => el.removeEventListener("focus", onFocus);
  }, [copyPasteContext]);

  const isSequencePasteContextActive = useCallback(
    (force = false) => force || copyPasteContext?.pasteContext === "sequence",
    [copyPasteContext?.pasteContext],
  );

  const getViewportCenterFlowPosition = useCallback(
    (
      fallback: { x: number; y: number } = PASTE_OFFSET,
      offset: { x: number; y: number } = { x: 0, y: 0 },
    ) => {
      try {
        const bounds = document
          .querySelector(".react-flow")
          ?.getBoundingClientRect();
        if (!bounds) {
          return fallback;
        }

        return screenToFlowPosition({
          x: bounds.left + bounds.width / 2 + offset.x,
          y: bounds.top + bounds.height / 2 + offset.y,
        });
      } catch {
        return fallback;
      }
    },
    [screenToFlowPosition],
  );

  const handleCopy = useCallback(
    (options?: { force?: boolean }) => {
      if (!isSequencePasteContextActive(options?.force)) return;
      if (
        selectedNodes.length === 0 &&
        selectedCommentZoneIds.length === 0 &&
        selectedTextboxIds.length === 0
      ) {
        return;
      }
      copyPasteContext?.clearPendingCut?.();
      const eligibleNodes = getEligibleNodes();
      if (
        eligibleNodes.length === 0 &&
        selectedCommentZoneIds.length === 0 &&
        selectedTextboxIds.length === 0
      ) {
        toast.info("Structural Sequence nodes cannot be copied", {
          position: "top-right",
        });
        return;
      }
      const selectedIds = new Set(eligibleNodes.map((n) => n.id));
      const selectedZoneIdSet = new Set(selectedCommentZoneIds);
      const selectedTextboxIdSet = new Set(selectedTextboxIds);
      const edgesBetween = edges.filter(
        (e) => selectedIds.has(e.source) && selectedIds.has(e.target),
      );
      if (
        eligibleNodes.length === 1 &&
        selectedCommentZoneIds.length === 0 &&
        selectedTextboxIds.length === 0
      ) {
        const sourceInteraction = eligibleNodes[0].data.interaction;
        const localization = buildLocalizationSnapshot(sourceInteraction);
        writeClipboard({
          version: CLIPBOARD_VERSION,
          kind: "interaction",
          payload: sourceInteraction,
          ...(localization && { localization }),
          ...buildDependencyMetadata(sourceInteraction),
        });
        toast.success("Node copied to clipboard", { position: "top-right" });
        return;
      }
      const payload: SequenceNodesPayload = {
        nodes: eligibleNodes.map((n) => ({
          id: n.id,
          position: n.position,
          data: {
            interaction: n.data.interaction,
            name: n.data.name,
            ...(n.data.color != null && { color: n.data.color }),
          },
        })),
        commentZones: commentZones
          .filter((zone) => selectedZoneIdSet.has(zone.id))
          .map((zone) => ({
            id: zone.id,
            position: zone.position,
            size: zone.size,
            label: zone.label,
            ...(zone.color != null && { color: zone.color }),
          })),
        textboxes: textboxes
          .filter((textbox) => selectedTextboxIdSet.has(textbox.id))
          .map((textbox) => ({
            id: textbox.id,
            position: textbox.position,
            size: textbox.size,
            text: textbox.text,
            ...(textbox.color != null && { color: textbox.color }),
          })),
        edges: edgesBetween.map((e) => ({
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle ?? undefined,
          targetHandle: e.targetHandle ?? undefined,
        })),
      };
      const localization = buildLocalizationSnapshot(
        payload.nodes.map((node) => node.data.interaction),
      );
      writeClipboard({
        version: CLIPBOARD_VERSION,
        kind: "sequenceNodes",
        payload,
        ...(localization && { localization }),
        ...buildDependencyMetadata(
          payload.nodes.map((node) => node.data.interaction),
        ),
      });
      const copiedItemsCount =
        eligibleNodes.length +
        selectedCommentZoneIds.length +
        selectedTextboxIds.length;
      toast.success(`${copiedItemsCount} items copied to clipboard`, {
        position: "top-right",
      });
    },
    [
      commentZones,
      copyPasteContext,
      currentSelectedNodes,
      edges,
      getEligibleNodes,
      isSequencePasteContextActive,
      selectedCommentZoneIds,
      selectedTextboxIds,
      textboxes,
      buildLocalizationSnapshot,
      buildDependencyMetadata,
    ],
  );

  const handleCut = useCallback(
    (options?: { force?: boolean }) => {
      if (!isSequencePasteContextActive(options?.force)) return;
      const source = getActiveLocator();
      if (!source) return;

      const eligibleNodes = getEligibleNodes();
      const protectedNodeCount = selectedNodes.length - eligibleNodes.length;
      const selectedZoneIdSet = new Set(selectedCommentZoneIds);
      const selectedTextboxIdSet = new Set(selectedTextboxIds);
      const selectedCommentZones = commentZones.filter((zone) =>
        selectedZoneIdSet.has(zone.id),
      );
      const selectedTextboxes = textboxes.filter((textbox) =>
        selectedTextboxIdSet.has(textbox.id),
      );
      if (
        eligibleNodes.length === 0 &&
        selectedCommentZones.length === 0 &&
        selectedTextboxes.length === 0
      ) {
        toast.info("Select a movable node, comment box, or text note to cut", {
          position: "top-right",
        });
        return;
      }

      const selectedNodeIds = new Set(eligibleNodes.map((node) => node.id));
      const selection: SequenceSelectionIds = {
        nodeIds: [...selectedNodeIds],
        commentZoneIds: selectedCommentZones.map((zone) => zone.id),
        textboxIds: selectedTextboxes.map((textbox) => textbox.id),
      };
      const payload: SequenceNodesPayload = {
        nodes: eligibleNodes.map((node) => ({
          id: node.id,
          position: node.position,
          data: {
            interaction: node.data.interaction,
            name: node.data.name,
            ...(node.data.color != null && { color: node.data.color }),
          },
        })),
        commentZones: selectedCommentZones.map((zone) => ({
          id: zone.id,
          position: zone.position,
          size: zone.size,
          label: zone.label,
          ...(zone.color != null && { color: zone.color }),
        })),
        textboxes: selectedTextboxes.map((textbox) => ({
          id: textbox.id,
          position: textbox.position,
          size: textbox.size,
          text: textbox.text,
          ...(textbox.color != null && { color: textbox.color }),
        })),
        edges: edges
          .filter(
            (edge) =>
              selectedNodeIds.has(edge.source) &&
              selectedNodeIds.has(edge.target),
          )
          .map((edge) => ({
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle ?? undefined,
            targetHandle: edge.targetHandle ?? undefined,
          })),
      };
      const cutId = uuidv4();
      const localization = buildLocalizationSnapshot(
        payload.nodes.map((node) => node.data.interaction),
      );
      writeClipboard({
        version: CLIPBOARD_VERSION,
        kind: "sequenceNodes",
        operation: "cut",
        cutId,
        payload,
        ...(localization && { localization }),
        ...buildDependencyMetadata(
          payload.nodes.map((node) => node.data.interaction),
        ),
      });
      copyPasteContext?.beginPendingCut?.({
        kind: "sequenceSelection",
        cutId,
        source,
        selection,
      });

      if (protectedNodeCount > 0) {
        toast.info("Protected Sequence nodes were left in place", {
          position: "top-right",
        });
      } else {
        toast.success(
          "Selection ready to move. Paste it or press Esc to cancel.",
          {
            position: "top-right",
          },
        );
      }
    },
    [
      commentZones,
      copyPasteContext,
      currentSelectedNodes,
      edges,
      getActiveLocator,
      getEligibleNodes,
      isSequencePasteContextActive,
      selectedCommentZoneIds,
      selectedNodes.length,
      selectedTextboxIds,
      textboxes,
      buildLocalizationSnapshot,
      buildDependencyMetadata,
    ],
  );

  const handlePaste = useCallback(
    async (options?: { force?: boolean; useViewportCenter?: boolean }) => {
      if (!isSequencePasteContextActive(options?.force)) return;
      if (!interaction || interaction.type !== InteractionType.Sequence) return;
      const envelope = await readClipboardEnvelopeFromClipboard();
      if (!envelope) return;
      const localizationSource = getLocalizationSource(envelope);

      const pendingSequenceCut =
        envelope.kind === "sequenceNodes" &&
        envelope.operation === "cut" &&
        Boolean(envelope.cutId) &&
        copyPasteContext?.pendingCut?.kind === "sequenceSelection" &&
        copyPasteContext.pendingCut.cutId === envelope.cutId
          ? copyPasteContext.pendingCut
          : null;

      if (pendingSequenceCut) {
        const destination = getActiveLocator();
        if (!destination) {
          toast.info("Open a Sequence before pasting this selection", {
            position: "top-right",
          });
          return;
        }

        let targetOrigin = PASTE_OFFSET;
        try {
          if (options?.useViewportCenter) {
            targetOrigin = getViewportCenterFlowPosition(PASTE_OFFSET);
          } else if (lastMouseScreenRef.current) {
            targetOrigin = screenToFlowPosition(lastMouseScreenRef.current);
          } else {
            targetOrigin = getViewportCenterFlowPosition(PASTE_OFFSET);
          }
        } catch {
          // Keep the stable fallback origin.
        }

        const movePayload = {
          source: pendingSequenceCut.source,
          destination,
          selection: pendingSequenceCut.selection,
          targetOrigin,
        };
        const resolution = moveSequenceSelection(takes, movePayload);
        if (!resolution.ok) {
          if (
            resolution.reason === "missingSource" ||
            resolution.reason === "missingItems"
          ) {
            copyPasteContext?.clearPendingCut?.();
            toast.error("The cut selection can no longer be moved", {
              position: "top-right",
            });
          } else if (resolution.reason === "destinationCollision") {
            toast.error(
              "The destination contains conflicting Sequence item IDs",
              {
                position: "top-right",
              },
            );
          } else {
            toast.info("Open another Sequence before pasting this selection", {
              position: "top-right",
            });
          }
          return;
        }

        dispatch(moveSequenceSelectionAction(movePayload));
        setSelection?.({
          nodeIds: resolution.movedNodes.map((node) => node.id),
          commentZoneIds: resolution.movedCommentZones.map((zone) => zone.id),
          textboxIds: resolution.movedTextboxes.map((textbox) => textbox.id),
        });
        copyPasteContext?.clearPendingCut?.();
        writeClipboard({
          ...envelope,
          operation: "copy",
          cutId: undefined,
        });
        toast.success("Sequence selection moved", { position: "top-right" });
        return;
      }

      const currentNodes = interaction.nodes ?? [];
      const currentEdges = interaction.edges ?? [];

      if (envelope.kind === "interaction") {
        const sourceInteraction = envelope.payload as Interaction;
        if (
          isProtectedSequenceStructureInteraction(
            sourceInteraction,
            selectedElementType,
          )
        ) {
          toast.info("Structural Sequence nodes cannot be pasted", {
            position: "top-right",
          });
          return;
        }
        const resolution = await resolveClipboardDependencies(
          sourceInteraction,
          envelope,
          store.getState(),
        );
        const sanitizedInteraction = resolution.value;
        const transaction = { id: createClipboardPasteTransactionId() };
        dispatch(beginHistoryTransaction(transaction));
        try {
          const copy =
            envelope.version === 2
              ? getInteractionCopy(sanitizedInteraction, localizationSource)
              : getInteractionCopy(sanitizedInteraction);
          if (!copy) {
            dispatch(cancelHistoryTransaction(transaction));
            return;
          }
          const newNodeId = `node-${uuidv4()}`;
          let position = PASTE_OFFSET;
          try {
            if (options?.useViewportCenter) {
              position = getViewportCenterFlowPosition(PASTE_OFFSET, {
                x: -128,
                y: -24,
              });
            } else if (lastMouseScreenRef.current) {
              position = screenToFlowPosition(lastMouseScreenRef.current);
            } else {
              position = getViewportCenterFlowPosition(PASTE_OFFSET, {
                x: -128,
                y: -24,
              });
            }
          } catch {
            // use offset
          }
          const newNode = createSequenceClipboardNode(
            newNodeId,
            position,
            copy,
          );
          updateSequence({
            ...interaction,
            nodes: [...currentNodes, newNode],
            edges: currentEdges,
            commentZones,
          } as ISequence);
          setSelection?.({
            nodeIds: [newNodeId],
            commentZoneIds: [],
            textboxIds: [],
          });
          dispatch(commitHistoryTransaction(transaction));
          if (envelope.operation === "cut") {
            copyPasteContext?.clearPendingCut?.();
            writeClipboard({
              ...envelope,
              operation: "copy",
              cutId: undefined,
            });
          }
          showPasteOutcome(
            "Node pasted",
            resolution,
            sourceInteraction,
            envelope,
          );
        } catch {
          dispatch(cancelHistoryTransaction(transaction));
          toast.error("Unable to paste Sequence node", {
            position: "top-right",
          });
        }
        return;
      }

      if (envelope.kind === "sequenceNodes") {
        const payload = envelope.payload as SequenceNodesPayload;
        const eligiblePayloadNodes = payload.nodes.filter(
          (node) =>
            !isProtectedSequenceStructureInteraction(
              node.data.interaction as Interaction,
              selectedElementType,
            ),
        );
        // Allocate node IDs before sanitizing interactions so local Play/Stop
        // Sub Sequence targets and pasted edges share one stable mapping.
        const idMap = new Map<string, string>(
          eligiblePayloadNodes.map((node) => [node.id, `node-${uuidv4()}`]),
        );
        const sourceInteractions = eligiblePayloadNodes.map(
          (node) => node.data.interaction as Interaction,
        );
        const resolution = await resolveClipboardDependencies(
          sourceInteractions,
          envelope,
          store.getState(),
          { nodeIdMap: idMap },
        );
        const transaction = { id: createClipboardPasteTransactionId() };
        dispatch(beginHistoryTransaction(transaction));
        try {
          const newNodes: Node<SequenceNodeData>[] = [];
          const newCommentZones: SequenceCommentZone[] = [];
          const newTextboxes: SequenceTextbox[] = [];
          let minX = Infinity;
          let minY = Infinity;
          for (let i = 0; i < eligiblePayloadNodes.length; i++) {
            const n = eligiblePayloadNodes[i];
            const sourceInteraction = resolution.value[i];
            const copy =
              envelope.version === 2
                ? getInteractionCopy(sourceInteraction, localizationSource)
                : getInteractionCopy(sourceInteraction);
            if (!copy) {
              idMap.delete(n.id);
              continue;
            }
            const newId = idMap.get(n.id)!;
            const pos = n.position;
            if (pos.x < minX) minX = pos.x;
            if (pos.y < minY) minY = pos.y;
            newNodes.push(
              createSequenceClipboardNode(newId, pos, copy, {
                color: n.data.color,
              }),
            );
          }
          for (const zone of payload.commentZones ?? []) {
            newCommentZones.push({
              ...zone,
              id: `comment-zone-${uuidv4()}`,
              position: { ...zone.position },
              size: { ...zone.size },
            });
            if (zone.position.x < minX) minX = zone.position.x;
            if (zone.position.y < minY) minY = zone.position.y;
          }
          for (const textbox of payload.textboxes ?? []) {
            newTextboxes.push({
              ...textbox,
              id: `textbox-${uuidv4()}`,
              position: { ...textbox.position },
              size: { ...textbox.size },
            });
            if (textbox.position.x < minX) minX = textbox.position.x;
            if (textbox.position.y < minY) minY = textbox.position.y;
          }
          if (
            newNodes.length === 0 &&
            newCommentZones.length === 0 &&
            newTextboxes.length === 0
          ) {
            dispatch(cancelHistoryTransaction(transaction));
            toast.info("Structural Sequence nodes cannot be pasted", {
              position: "top-right",
            });
            return;
          }
          const newEdges = payload.edges
            .map((e): PastedEdge | null => {
              const newSource = idMap.get(e.source);
              const newTarget = idMap.get(e.target);
              if (!newSource || !newTarget) return null;
              return {
                id: `e-${newSource}-${newTarget}-${uuidv4()}`,
                source: newSource,
                target: newTarget,
                sourceHandle: e.sourceHandle ?? null,
                targetHandle: e.targetHandle ?? null,
                type: "custom" as const,
              };
            })
            .filter((e): e is PastedEdge => e !== null);

          let offsetX = minX !== Infinity ? PASTE_OFFSET.x - minX : 0;
          let offsetY = minY !== Infinity ? PASTE_OFFSET.y - minY : 0;
          try {
            if (options?.useViewportCenter) {
              const centerFlow = getViewportCenterFlowPosition(PASTE_OFFSET);
              offsetX = minX !== Infinity ? centerFlow.x - minX : centerFlow.x;
              offsetY = minY !== Infinity ? centerFlow.y - minY : centerFlow.y;
            } else if (lastMouseScreenRef.current) {
              const cursorFlow = screenToFlowPosition(
                lastMouseScreenRef.current,
              );
              offsetX = minX !== Infinity ? cursorFlow.x - minX : cursorFlow.x;
              offsetY = minY !== Infinity ? cursorFlow.y - minY : cursorFlow.y;
            }
          } catch {
            // keep default offset
          }
          const nodesWithPosition = newNodes.map((node) => ({
            ...node,
            position: {
              x: node.position.x + offsetX,
              y: node.position.y + offsetY,
            },
          }));
          const commentZonesWithPosition = newCommentZones.map((zone) => ({
            ...zone,
            position: {
              x: zone.position.x + offsetX,
              y: zone.position.y + offsetY,
            },
          }));
          const textboxesWithPosition = newTextboxes.map((textbox) => ({
            ...textbox,
            position: {
              x: textbox.position.x + offsetX,
              y: textbox.position.y + offsetY,
            },
          }));
          updateSequence({
            ...interaction,
            nodes: [...currentNodes, ...nodesWithPosition],
            edges: [...currentEdges, ...newEdges],
            commentZones: [...commentZones, ...commentZonesWithPosition],
            textboxes: [...textboxes, ...textboxesWithPosition],
          } as ISequence);
          setSelection?.({
            nodeIds: nodesWithPosition.map((node) => node.id),
            commentZoneIds: commentZonesWithPosition.map((zone) => zone.id),
            textboxIds: textboxesWithPosition.map((textbox) => textbox.id),
          });
          dispatch(commitHistoryTransaction(transaction));
          const successMessage = `${
            nodesWithPosition.length +
            commentZonesWithPosition.length +
            textboxesWithPosition.length
          } items pasted`;
          if (envelope.operation === "cut") {
            copyPasteContext?.clearPendingCut?.();
            writeClipboard({
              ...envelope,
              operation: "copy",
              cutId: undefined,
            });
          }
          showPasteOutcome(
            successMessage,
            resolution,
            payload.nodes.map((node) => node.data.interaction),
            envelope,
          );
        } catch {
          dispatch(cancelHistoryTransaction(transaction));
          toast.error("Unable to paste Sequence selection", {
            position: "top-right",
          });
        }
      }
    },
    [
      commentZones,
      copyPasteContext,
      getViewportCenterFlowPosition,
      getActiveLocator,
      getInteractionCopy,
      getLocalizationSource,
      interaction,
      isSequencePasteContextActive,
      screenToFlowPosition,
      setSelection,
      selectedElementType,
      dispatch,
      takes,
      textboxes,
      updateSequence,
      showPasteOutcome,
      store,
    ],
  );

  const handleDuplicate = useCallback(
    (options?: { force?: boolean; useViewportCenter?: boolean }) => {
      if (!isSequencePasteContextActive(options?.force)) return;
      if (!interaction || interaction.type !== InteractionType.Sequence) return;
      if (
        selectedNodes.length === 0 &&
        selectedCommentZoneIds.length === 0 &&
        selectedTextboxIds.length === 0
      ) {
        return;
      }

      const currentNodes = interaction.nodes ?? [];
      const currentEdges = interaction.edges ?? [];
      const currentCommentZones = interaction.commentZones ?? [];
      const currentTextboxes = interaction.textboxes ?? [];
      const eligibleNodes = getEligibleNodes();
      const selectedIds = new Set(eligibleNodes.map((n) => n.id));
      const selectedZoneIdSet = new Set(selectedCommentZoneIds);
      const selectedTextboxIdSet = new Set(selectedTextboxIds);
      const edgesBetween = edges.filter(
        (e) => selectedIds.has(e.source) && selectedIds.has(e.target),
      );

      const idMap = new Map<string, string>();
      const newNodes: Node<SequenceNodeData>[] = [];
      const newCommentZones: SequenceCommentZone[] = [];
      const newTextboxes: SequenceTextbox[] = [];
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      for (const node of eligibleNodes) {
        const copy = getInteractionCopy(node.data.interaction);
        if (!copy) continue;
        const newId = `node-${uuidv4()}`;
        idMap.set(node.id, newId);
        const pos = node.position;
        if (pos.x < minX) minX = pos.x;
        if (pos.y < minY) minY = pos.y;
        const nodeWidth = node.measured?.width ?? node.width ?? 256;
        const nodeHeight = node.measured?.height ?? node.height ?? 96;
        if (pos.x + nodeWidth > maxX) maxX = pos.x + nodeWidth;
        if (pos.y + nodeHeight > maxY) maxY = pos.y + nodeHeight;
        newNodes.push(
          createSequenceClipboardNode(newId, pos, copy, {
            color: node.data.color,
          }),
        );
      }

      for (const zone of currentCommentZones) {
        if (!selectedZoneIdSet.has(zone.id)) continue;
        newCommentZones.push({
          ...zone,
          id: `comment-zone-${uuidv4()}`,
          position: { ...zone.position },
          size: { ...zone.size },
        });
        if (zone.position.x < minX) minX = zone.position.x;
        if (zone.position.y < minY) minY = zone.position.y;
        if (zone.position.x + zone.size.width > maxX) {
          maxX = zone.position.x + zone.size.width;
        }
        if (zone.position.y + zone.size.height > maxY) {
          maxY = zone.position.y + zone.size.height;
        }
      }

      for (const textbox of currentTextboxes) {
        if (!selectedTextboxIdSet.has(textbox.id)) continue;
        newTextboxes.push({
          ...textbox,
          id: `textbox-${uuidv4()}`,
          position: { ...textbox.position },
          size: { ...textbox.size },
        });
        if (textbox.position.x < minX) minX = textbox.position.x;
        if (textbox.position.y < minY) minY = textbox.position.y;
        if (textbox.position.x + textbox.size.width > maxX) {
          maxX = textbox.position.x + textbox.size.width;
        }
        if (textbox.position.y + textbox.size.height > maxY) {
          maxY = textbox.position.y + textbox.size.height;
        }
      }

      if (
        newNodes.length === 0 &&
        newCommentZones.length === 0 &&
        newTextboxes.length === 0
      ) {
        return;
      }

      const newEdges = edgesBetween
        .map((e): PastedEdge | null => {
          const newSource = idMap.get(e.source);
          const newTarget = idMap.get(e.target);
          if (!newSource || !newTarget) return null;
          return {
            id: `e-${newSource}-${newTarget}-${uuidv4()}`,
            source: newSource,
            target: newTarget,
            sourceHandle: e.sourceHandle ?? null,
            targetHandle: e.targetHandle ?? null,
            type: "custom" as const,
          };
        })
        .filter((e): e is PastedEdge => e !== null);

      let offsetX = minX !== Infinity ? PASTE_OFFSET.x : 0;
      let offsetY = minY !== Infinity ? PASTE_OFFSET.y : 0;
      if (
        options?.useViewportCenter &&
        minX !== Infinity &&
        minY !== Infinity &&
        maxX !== -Infinity &&
        maxY !== -Infinity
      ) {
        const groupCenter = {
          x: minX + (maxX - minX) / 2,
          y: minY + (maxY - minY) / 2,
        };
        const viewportCenter = getViewportCenterFlowPosition(PASTE_OFFSET);
        offsetX = viewportCenter.x - groupCenter.x;
        offsetY = viewportCenter.y - groupCenter.y;
      }
      const nodesWithPosition = newNodes.map((node) => ({
        ...node,
        position: {
          x: node.position.x + offsetX,
          y: node.position.y + offsetY,
        },
      }));
      const commentZonesWithPosition = newCommentZones.map((zone) => ({
        ...zone,
        position: {
          x: zone.position.x + offsetX,
          y: zone.position.y + offsetY,
        },
      }));
      const textboxesWithPosition = newTextboxes.map((textbox) => ({
        ...textbox,
        position: {
          x: textbox.position.x + offsetX,
          y: textbox.position.y + offsetY,
        },
      }));
      updateSequence({
        ...interaction,
        nodes: [...currentNodes, ...nodesWithPosition],
        edges: [...currentEdges, ...newEdges],
        commentZones: [...currentCommentZones, ...commentZonesWithPosition],
        textboxes: [...currentTextboxes, ...textboxesWithPosition],
      } as ISequence);
      setSelection?.({
        nodeIds: nodesWithPosition.map((node) => node.id),
        commentZoneIds: commentZonesWithPosition.map((zone) => zone.id),
        textboxIds: textboxesWithPosition.map((textbox) => textbox.id),
      });
      toast.success(
        `${
          nodesWithPosition.length +
          commentZonesWithPosition.length +
          textboxesWithPosition.length
        } items duplicated`,
        {
          position: "top-right",
        },
      );
    },
    [
      edges,
      getEligibleNodes,
      getViewportCenterFlowPosition,
      getInteractionCopy,
      interaction,
      isSequencePasteContextActive,
      selectedCommentZoneIds,
      selectedTextboxIds,
      setSelection,
      textboxes,
      updateSequence,
    ],
  );

  return {
    handleCopy,
    handleCut,
    handlePaste,
    handleDuplicate,
    setSequenceCanvasRef,
    onPaneMouseMove,
  };
}
